import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { tyresAPI } from '../services/api';

const link = (vehicle, extra) => {
  const p = new URLSearchParams({ type: 'vehicule', brand: vehicle.brand, model: vehicle.model, ...(vehicle.year ? { year: String(vehicle.year) } : {}), ...(vehicle.engine?.name ? { engine: vehicle.engine.name } : {}), ...extra });
  return `/pieces/pneus-jantes-et-accessoires?${p.toString()}`;
};

const VehicleTyreSizesCard = ({ vehicle }) => {
  const [fitment, setFitment] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = vehicle ? `${vehicle.brand}|${vehicle.model}|${vehicle.year}|${vehicle.engine?.name}` : '';

  useEffect(() => {
    if (!vehicle?.brand || !vehicle?.model) { setFitment(null); return undefined; }
    let alive = true;
    setLoading(true); setFitment(null);
    tyresAPI.vehicle({ brand: vehicle.brand, model: vehicle.model, year: vehicle.year || undefined, engine: vehicle.engine?.name || undefined })
      .then((f) => { if (alive) setFitment(f); }).catch(() => { if (alive) setFitment(null); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [key]);

  if (!vehicle) return null;
  if (loading) {
    return <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center gap-2 text-sm text-gray-500" data-testid="vehicle-tyre-sizes-loading"><Loader2 size={16} className="animate-spin text-orange-500" /> Recherche des dimensions de pneus et jantes d'origine…</div>;
  }
  if (!fitment?.found || (!fitment.tyre_sizes.length && !fitment.wheel_sizes.length)) return null;
  const bolt = fitment.bolt_pattern || {};
  return (
    <section className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="vehicle-tyre-sizes">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-base md:text-lg font-bold text-gray-900">Dimensions pneus & jantes d'origine</h2>
        <Link to={link(vehicle, { kind: 'tyre' })} data-testid="vehicle-tyre-sizes-all" className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:underline">Voir les pneus pour ce véhicule <ArrowRight size={14} /></Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 mb-2"><img src="/images/selector/tyres.png" alt="Pneus" className="h-12 w-12 object-contain no-placeholder" data-testid="vehicle-tyre-sizes-tyre-image" /> Pneus</p>
          <div className="flex flex-wrap gap-1.5">
            {fitment.tyre_sizes.map((s) => (
              <Link key={s.label} to={link(vehicle, { kind: 'tyre', size: s.label })} data-testid={`vehicle-tyre-size-${s.width}${s.height}${s.diameter}`}
                className="rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 font-semibold text-orange-900 hover:bg-orange-100" title={s.count ? `${s.count} pneu(s) disponible(s)` : 'Aucun pneu en stock pour l’instant'}>
                {s.label}{s.count ? <span className="ml-1 text-xs font-normal text-green-700">({s.count})</span> : null}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 mb-2"><img src="/images/selector/rim.png" alt="Jantes" className="h-12 w-12 object-contain no-placeholder" data-testid="vehicle-tyre-sizes-rim-image" /> Jantes {bolt.label && <span className="normal-case font-normal text-gray-500">· entraxe {bolt.label}{bolt.center_bore ? ` · alésage ${String(bolt.center_bore).replace('.', ',')} mm` : ''}{bolt.nut_thread ? ` · ${bolt.nut_thread}` : ''}</span>}</p>
          <div className="flex flex-wrap gap-1.5">
            {fitment.wheel_sizes.map((s) => (
              <Link key={s.label} to={link(vehicle, { kind: 'wheel', size: s.label })} data-testid={`vehicle-wheel-size-${String(s.width).replace('.', '')}${s.diameter}`}
                className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 font-semibold text-sky-900 hover:bg-sky-100">{s.label}</Link>
            ))}
            {!fitment.wheel_sizes.length && <span className="text-gray-400">—</span>}
          </div>
        </div>
      </div>
      {fitment.ai_unverified && <p className="mt-3 flex items-center gap-1 text-[11px] text-amber-700" data-testid="vehicle-tyre-sizes-ai"><Sparkles size={12} /> Données établies automatiquement, en attente de vérification — confirmez la dimension sur le flanc de votre pneu.</p>}
    </section>
  );
};

export default VehicleTyreSizesCard;
